import {
  currencyFormatter,
  noDecimalCurrencyFormatter,
  noDecimalNumberFormatter,
  numberFormatter,
  numberNoRoundingFormatter,
  percentFormatter,
  percentNoDecimalFormatter,
  percentTwoDecimalFormatter,
} from "@intelligems/ig-graphs/data-formatters";
import { DataType } from "@intelligems/ig-types/schemas/analytics/custom-graphs";

/**
 * Format metric value based on data type (supports all DataType values)
 * @param value - The numeric value to format (can be null/undefined)
 * @param dataType - Type of metric (supports all DataType values)
 * @param currency - Currency configuration object
 * @param fallbackValue - Value to return if value is null/undefined (defaults to "—")
 * @returns Formatted string or fallback value if value is null/undefined
 */
export const formatMetricValue = (
  value: number | null | undefined,
  dataType: DataType,
  currency: { code: string; locale: string },
  fallbackValue: string = "n/a"
): string => {
  if (value === null || value === undefined) {
    return fallbackValue;
  }

  switch (dataType) {
    case "currency":
      return currencyFormatter(value, false, currency);
    case "currencyNoDecimal":
      return noDecimalCurrencyFormatter(value, false, currency);
    case "percent":
      return percentFormatter(value, false, currency);
    case "percentTwoDecimal":
      return percentTwoDecimalFormatter(value, false, currency);
    case "percentNoDecimal":
      return percentNoDecimalFormatter(value, true, currency);
    case "numberNoDecimal":
      return noDecimalNumberFormatter(value, false, currency);
    case "numberNoRounding":
      return numberNoRoundingFormatter(value, false, currency);
    case "number":
      return numberFormatter(value, false, currency);
    case "none":
      return String(value);
    default:
      return numberFormatter(value, false, currency);
  }
};

/**
 * Format uplift percentage with sign
 * @param uplift - The uplift value (can be null/undefined)
 * @param currency - Currency configuration object
 * @returns Formatted percentage string with +/- sign, or empty string if no uplift
 */
export const formatUpliftPercentage = (
  uplift: number | null | undefined,
  currency: { code: string; locale: string }
): string => {
  if (uplift === null || uplift === undefined) {
    return "";
  }

  return `${percentNoDecimalFormatter(Math.abs(uplift), true, currency)}`;
};

/**
 * Format timestamp as a localized date label for mobile charts
 * @param timestamp - Unix timestamp or Date-compatible value
 * @param locale - Locale string (e.g., "en-US")
 * @param options - Optional configuration for formatting
 * @returns Formatted date string
 */
export const formatDateLabel = (
  timestamp: number | string,
  locale: string = "en-US",
  options?: {
    includeTime?: boolean;
    includeMinutes?: boolean;
    shortMonth?: boolean;
  }
): string => {
  const {
    includeTime = false,
    includeMinutes = false,
    shortMonth = false,
  } = options || {};

  const dateOptions: Intl.DateTimeFormatOptions = {
    month: shortMonth ? "short" : "numeric",
    day: "numeric",
    ...(includeTime && {
      hour: "numeric",
    }),
    ...(includeMinutes && {
      minute: "2-digit",
    }),
  };

  return new Date(timestamp).toLocaleDateString(locale, dateOptions);
};
